// frontend/src/components/GenotypeManagement.js
// Mobile-First GenotypeManagement with responsive design

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { db } from '../services/db';
import { DataService } from '../services/DataService';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { ConfirmDialog } from './ConfirmDialog';

const ITEMS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const GenotypeManagement = () => {
  const toast = useToast();
  const { confirm, confirmDialogProps } = useConfirm();
  const [activeTab, setActiveTab] = useState('locus');
  const [data, setData] = useState({
    locus: [],
    allele: [],
    strain: [],
    allLocusAllelePairs: [],
    locusAlleleColors: {}
  });
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // `error` now carries only two inline things: the initial load failure, and
  // form validation on the add box. Both belong next to what they describe and
  // are cleared by the next submit. Every add/delete/color outcome is an action
  // result and goes to a toast.
  const [error, setError] = useState('');
  const [newItem, setNewItem] = useState('');

  // Mobile view state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Pagination states for Locus
  const [currentPageLocus, setCurrentPageLocus] = useState(1);
  const [itemsPerPageLocus, setItemsPerPageLocus] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [totalLocusCount, setTotalLocusCount] = useState(0);

  // Sorting states for Locus
  const [sortColumnLocus, setSortColumnLocus] = useState('locus_name');
  const [sortOrderLocus, setSortOrderLocus] = useState('asc');

  // Pagination states for Allele
  const [currentPageAllele, setCurrentPageAllele] = useState(1);
  const [itemsPerPageAllele, setItemsPerPageAllele] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [totalAlleleCount, setTotalAlleleCount] = useState(0);

  // Sorting states for Allele
  const [sortColumnAllele, setSortColumnAllele] = useState('allele_name');
  const [sortOrderAllele, setSortOrderAllele] = useState('asc');

  // States for Strain
  const [currentPageStrain, setCurrentPageStrain] = useState(1);
  const [itemsPerPageStrain, setItemsPerPageStrain] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [totalStrainCount, setTotalStrainCount] = useState(0);
  const [sortColumnStrain, setSortColumnStrain] = useState('strain_name');
  const [sortOrderStrain, setSortOrderStrain] = useState('asc');
  const [searchQueryStrain, setSearchQueryStrain] = useState('');


  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // Check for mobile view on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // On mount: sync to populate Dexie before reading locus/allele
  useEffect(() => {
    if (navigator.onLine) DataService.sync();
  }, []);

  // Re-fetch locus/allele when a background sync completes
  useEffect(() => {
    const onSync = () => {
      if (activeTab === 'locus') fetchLocusFromDexie();
      else if (activeTab === 'allele') fetchAlleleFromDexie();
    };
    window.addEventListener('squeakbase-sync-complete', onSync);
    return () => window.removeEventListener('squeakbase-sync-complete', onSync);
  }, [activeTab]);

  useEffect(() => {
    fetchAllData();
  }, [
    currentPageLocus, itemsPerPageLocus, sortColumnLocus, sortOrderLocus,
    currentPageAllele, itemsPerPageAllele, sortColumnAllele, sortOrderAllele,
    currentPageStrain, itemsPerPageStrain, sortColumnStrain, sortOrderStrain, searchQueryStrain,
    activeTab
  ]);

  // Read locus data from local Dexie cache and apply in-memory sort + pagination.
  // Locus/allele are synced into Dexie by DataService — no network request needed.
  const fetchLocusFromDexie = async () => {
    const allItems = await db.locus.toArray();
    // Hide deactivated rows (`!== false` so pre-migration cached rows stay visible)
    const activeItems = allItems.filter(x => x.is_active !== false);
    const sorted = [...activeItems].sort((a, b) => {
      const valA = (a[sortColumnLocus] ?? '').toString().toLowerCase();
      const valB = (b[sortColumnLocus] ?? '').toString().toLowerCase();
      return sortOrderLocus === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    setTotalLocusCount(sorted.length);
    const offset = (currentPageLocus - 1) * itemsPerPageLocus;
    setData(prevData => ({ ...prevData, locus: sorted.slice(offset, offset + itemsPerPageLocus) }));
  };

  const fetchAlleleFromDexie = async () => {
    const allItems = await db.allele.toArray();
    // Hide deactivated rows (`!== false` so pre-migration cached rows stay visible)
    const activeItems = allItems.filter(x => x.is_active !== false);
    const sorted = [...activeItems].sort((a, b) => {
      const valA = (a[sortColumnAllele] ?? '').toString().toLowerCase();
      const valB = (b[sortColumnAllele] ?? '').toString().toLowerCase();
      return sortOrderAllele === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    setTotalAlleleCount(sorted.length);
    const offset = (currentPageAllele - 1) * itemsPerPageAllele;
    setData(prevData => ({ ...prevData, allele: sorted.slice(offset, offset + itemsPerPageAllele) }));
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'locus') {
        await fetchLocusFromDexie();
      } else if (activeTab === 'allele') {
        await fetchAlleleFromDexie();
      } else if (activeTab === 'pairs') {
        // Pairs and colors require a network connection (relational joins not in Dexie)
        const bust = `_t=${Date.now()}`;
        const [locusItems, alleleItems, pairsRes, colorsRes] = await Promise.all([
          (await db.locus.toArray()).filter(x => x.is_active !== false),
          (await db.allele.toArray()).filter(x => x.is_active !== false),
          axios.get(`${process.env.REACT_APP_API_URL}/api/locus-allele-pairs-all?${bust}`),
          axios.get(`${process.env.REACT_APP_API_URL}/api/locus-allele-colors?${bust}`)
        ]);
        const colorsMap = colorsRes.data.reduce((acc, curr) => {
          acc[curr.locus_allele_pair_id] = curr.color_hex_code;
          return acc;
        }, {});
        setData(prevData => ({
          ...prevData,
          locus: locusItems,
          allele: alleleItems,
          allLocusAllelePairs: pairsRes.data,
          locusAlleleColors: colorsMap,
        }));
        setTotalLocusCount(locusItems.length);
        setTotalAlleleCount(alleleItems.length);
      } else if (activeTab === 'strain') {
        const strainParams = new URLSearchParams({
            _page: currentPageStrain,
            _limit: itemsPerPageStrain,
            _sort: sortColumnStrain,
            _order: sortOrderStrain,
            search: searchQueryStrain
        }).toString();
        const strainRes = await axios.get(`${process.env.REACT_APP_API_URL}/api/strains?${strainParams}`);
        setData(prevData => ({ ...prevData, strain: strainRes.data.data }));
        setTotalStrainCount(strainRes.data.totalCount);
      }
      setError('');
    } catch (err) {
      setError('Failed to load data');
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');

    if (isOffline) {
      toast.error('You must be online to add genotype data. Please check your internet connection.');
      return;
    }

    if (!newItem.trim()) {
      setError('Input cannot be empty.');
      return;
    }

    const itemsToAdd = newItem.split(',').map(item => item.trim()).filter(item => item);

    if (itemsToAdd.length === 0) {
      setError('No valid items to add after parsing.');
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = activeTab === 'strain' ? '/api/strains/bulk' : `/api/${activeTab}/bulk`;
      const fieldName = `${activeTab}_name`;

      const response = await axios.post(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        items: itemsToAdd.map(item => ({ [fieldName]: item }))
      });

      setNewItem('');

      const addedCount = response.data.added_count || 0;
      const skippedCount = response.data.skipped_count || 0;
      const reactivatedCount = response.data.reactivated_count || 0;

      // For locus/allele/strain: force a Dexie refresh so other components see the change
      if (activeTab === 'locus' || activeTab === 'allele') {
        await DataService.sync({ forceStaticRefresh: true });
        if (activeTab === 'locus') await fetchLocusFromDexie();
        else await fetchAlleleFromDexie();
      } else if (activeTab === 'strain') {
        await DataService.sync({ forceStaticRefresh: true });
        fetchAllData();
      } else {
        fetchAllData();
      }

      if (addedCount > 0 || reactivatedCount > 0) {
        let msg = `${addedCount} new ${activeTab}(s) added successfully!`;
        if (reactivatedCount > 0) msg += ` ${reactivatedCount} previously-deactivated ${activeTab}(s) re-activated.`;
        if (skippedCount > 0) msg += ` (${skippedCount} duplicate(s) skipped)`;
        toast.success(msg);
      } else if (skippedCount > 0) {
        toast.warn(`No new ${activeTab}(s) added. All provided were duplicates.`);
      } else {
        toast.error(`Failed to add ${activeTab}(s). Please try again.`);
      }

    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to add ${activeTab}(s)`);
      console.error('Add item error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = async (itemId, itemName, itemType) => {
    if (isOffline) {
      toast.error('You must be online to delete genotype data. Please check your internet connection.');
      return;
    }

    if (itemType === 'locus' || itemType === 'allele') {
      // Soft delete: the row is deactivated, not removed — archived mice keep their history.
      if (!await confirm(`Deactivate ${itemType} "${itemName}"? This will hide it from new entries; genotypes on existing mice are kept. You can re-add the name later to reactivate it.`, {
        confirmLabel: 'Deactivate'
      })) {
        return;
      }
    } else if (!await confirm(`Are you sure you want to delete ${itemType} "${itemName}"? This action cannot be undone.`, {
      confirmLabel: 'Delete',
      danger: true
    })) {
      return;
    }

    setDeletingId(itemId);

    try {
      const endpoint = itemType === 'strain' ? `/api/strains/${itemId}` : `/api/${itemType}/${itemId}`;
      const res = await axios.delete(`${process.env.REACT_APP_API_URL}${endpoint}`);
      if (itemType === 'locus' || itemType === 'allele') {
        const usageCount = res.data.usage_count;
        toast.success(usageCount !== undefined && usageCount > 0
          ? `${itemType} "${itemName}" deactivated — ${usageCount} ${usageCount === 1 ? 'mouse carries' : 'mice carry'} this genotype; their history is kept.`
          : `${itemType} "${itemName}" deactivated successfully!`);
        // Store the tombstone row (is_active: false) in Dexie so pickers hide it
        // immediately; incremental sync also propagates it to other devices.
        await db[itemType].put(res.data.locus || res.data.allele);
        await DataService.sync({ forceStaticRefresh: true });
        if (itemType === 'locus') await fetchLocusFromDexie();
        else await fetchAlleleFromDexie();
      } else if (itemType === 'strain') {
        toast.success(`${itemType} "${itemName}" deleted successfully!`);
        // Remove from Dexie directly (incremental sync won't catch deletions),
        // then force a full refresh to ensure consistency.
        await db.strains.delete(itemId);
        await DataService.sync({ forceStaticRefresh: true });
        fetchAllData();
      } else {
        fetchAllData();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to delete ${itemType} "${itemName}".`);
      console.error(`Delete ${itemType} error:`, err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleColorChange = async (locusAllelePairId, newColor) => {
    if (isOffline) {
      toast.error('You must be online to update colors. Please check your internet connection.');
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/locus-allele-colors`, {
        locus_allele_pair_id: locusAllelePairId,
        color_hex_code: newColor
      });
      setData(prevData => ({
        ...prevData,
        locusAlleleColors: {
          ...prevData.locusAlleleColors,
          [locusAllelePairId]: newColor
        }
      }));
      toast.success('Color updated successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update color.');
      console.error('Color update error:', err);
    }
  };

  // Mobile-specific rendering for locus/allele items
  const renderMobileItemList = () => {
    const currentData = data[activeTab] || [];
    const displayName = activeTab.replace('_', ' ').toUpperCase();

    return (
      <div className="mobile-list">
        {currentData.map((item) => (
          <div key={item.id} className="list-item">
            <div className="item-content">
              <div className="item-title">
                {item[`${activeTab}_name`] || 'Unknown'}
              </div>
              <div className="item-subtitle">
                {displayName}
              </div>
            </div>
            <div className="item-actions">
              <button
                onClick={() => handleDeleteItem(item.id, item[`${activeTab}_name`], activeTab)}
                className="action-btn"
                style={{ backgroundColor: '#dc3545', color: 'white' }}
                title={`Delete ${item[`${activeTab}_name`]}`}
                disabled={deletingId === item.id}
              >
                {deletingId === item.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Mobile-specific rendering for color pairs
  const renderMobileColorList = () => {
    const sortedPairs = [...data.allLocusAllelePairs].sort((a, b) => {
        const locusCompare = a.locus_name.localeCompare(b.locus_name);
        if (locusCompare !== 0) return locusCompare;
        return a.allele_name.localeCompare(b.allele_name);
    });

    return (
      <div className="mobile-list">
        {sortedPairs.map((pair) => {
          const currentColor = data.locusAlleleColors[pair.locus_allele_pair_id] || '#CCCCCC';
          return (
            <div key={pair.locus_allele_pair_id} className="list-item">
              <div className="item-content">
                <div className="item-title">
                  {pair.locus_name} ({pair.allele_name})
                </div>
                <div className="item-subtitle">
                  Color: {currentColor.toUpperCase()}
                </div>
              </div>
              <div className="item-actions">
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => handleColorChange(pair.locus_allele_pair_id, e.target.value)}
                  style={{ 
                    width: '40px', 
                    height: '40px', 
                    padding: 0, 
                    border: 'none', 
                    borderRadius: '8px',
                    cursor: 'pointer' 
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

const renderLocusAllelePairsTabContent = () => {
  const sortedPairs = [...data.allLocusAllelePairs].sort((a, b) => {
    const locusCompare = a.locus_name.localeCompare(b.locus_name);
    if (locusCompare !== 0) return locusCompare;
    return a.allele_name.localeCompare(b.allele_name);
  });

  return (
    <div className="genotype-tab-content">
      <h3>Assign Colors to Locus-Allele Pairs</h3>
      <p>Assign unique colors to your genotype pairs. These colors will appear next to the genotype on mouse cards.</p>
      
            {error && <div className="error">{error}</div>}

      {sortedPairs.length === 0 ? (
        <p className="no-data">No locus-allele pairs available. Add loci and alleles first!</p>
      ) : (
        <div className="genotype-list-container">
          {isMobile ? renderMobileColorList() : (
            <table className="genotype-table">
              <thead>
                <tr>
                  <th>Locus-Allele Pair</th>
                  <th style={{ width: '100px' }}>Assigned Color</th>
                  <th style={{ width: '200px', textAlign: 'center' }}>Change Color</th>
                </tr>
              </thead>
              <tbody>
                {sortedPairs.map((pair) => {
                  const currentColor = data.locusAlleleColors[pair.locus_allele_pair_id] || '#CCCCCC';
                  return (
                    <tr key={pair.locus_allele_pair_id}>
                      <td>{pair.locus_name} ({pair.allele_name})</td>
                      <td>
                        <div style={{
                          backgroundColor: currentColor,
                          width: '30px',
                          height: '30px',
                          borderRadius: '4px',
                          border: '1px solid #ccc',
                          display: 'inline-block',
                          verticalAlign: 'middle'
                        }}></div>
                        <span style={{ marginLeft: '10px', verticalAlign: 'middle' }}>{currentColor.toUpperCase()}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                          <input
                            type="color"
                            value={currentColor}
                            onChange={(e) => handleColorChange(pair.locus_allele_pair_id, e.target.value)}
                            style={{ width: '50px', height: '30px', padding: '0', border: 'none', background: 'none', cursor: 'pointer' }}
                          />
                          <input
                            type="text"
                            value={currentColor}
                            onChange={(e) => handleColorChange(pair.locus_allele_pair_id, e.target.value)}
                            style={{ width: '100px', padding: '5px' }}
                            placeholder="#HEXCODE"
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

  const handleSort = (tab, column) => {
    if (tab === 'locus') {
      if (sortColumnLocus === column) {
        setSortOrderLocus(sortOrderLocus === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumnLocus(column);
        setSortOrderLocus('asc');
      }
      setCurrentPageLocus(1);
    } else if (tab === 'allele') {
      if (sortColumnAllele === column) {
        setSortOrderAllele(sortOrderAllele === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumnAllele(column);
        setSortOrderAllele('asc');
      }
      setCurrentPageAllele(1);
    } else if (tab === 'strain') {
      if (sortColumnStrain === column) {
        setSortOrderStrain(sortOrderStrain === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumnStrain(column);
        setSortOrderStrain('asc');
      }
      setCurrentPageStrain(1);
    }
  };

  const handlePageChange = (tab, pageNumber) => {
    if (tab === 'locus') {
      setCurrentPageLocus(pageNumber);
    } else if (tab === 'allele') {
      setCurrentPageAllele(pageNumber);
    } else if (tab === 'strain') {
      setCurrentPageStrain(pageNumber);
    }
  };

  const handleItemsPerPageChange = (tab, e) => {
    const value = parseInt(e.target.value, 10);
    if (tab === 'locus') {
      setItemsPerPageLocus(value);
      setCurrentPageLocus(1);
    } else if (tab === 'allele') {
      setItemsPerPageAllele(value);
      setCurrentPageAllele(1);
    } else if (tab === 'strain') {
      setItemsPerPageStrain(value);
      setCurrentPageStrain(1);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQueryStrain(e.target.value);
    setCurrentPageStrain(1);
  }

  const renderTabContent = () => {
    if (activeTab === 'pairs') {
      return renderLocusAllelePairsTabContent();
    }

    const currentData = data[activeTab] || [];
    const displayName = activeTab.replace('_', ' ').toUpperCase();
    const placeholderText = `Enter comma-separated ${displayName.toLowerCase()} names (e.g., ${activeTab === 'locus' ? 'MHC, Agouti' : 'A, B'})`;
    
    const currentPage = activeTab === 'locus' ? currentPageLocus : currentPageAllele;
    const itemsPerPage = activeTab === 'locus' ? itemsPerPageLocus : itemsPerPageAllele;
    const totalCount = activeTab === 'locus' ? totalLocusCount : totalAlleleCount;
    const sortColumn = activeTab === 'locus' ? sortColumnLocus : sortColumnAllele;
    const sortOrder = activeTab === 'locus' ? sortOrderLocus : sortOrderAllele;
    const totalPages = Math.ceil(totalCount / itemsPerPage);

    if (activeTab === 'strain') {
      const totalPages = Math.ceil(totalStrainCount / itemsPerPageStrain);
      return (
        <div className="genotype-tab-content">
          <h3>Manage Strains ({totalStrainCount})</h3>

          <form onSubmit={handleAdd} className="add-item-form">
            <div className="form-group">
              <label>Strain Name(s):</label>
              <div className="input-group">
                <input
                  type="text"
                  value={newItem}
                  onChange={(e) => setNewItem(e.target.value)}
                  placeholder="Enter comma-separated strain names"
                  required
                />
                <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Adding...' : 'Add'}</button>
              </div>
            </div>
          </form>

                    {error && <div className="error">{error}</div>}

          <div className="items-list">
            <h4>Current Strain Options</h4>
            {data.strain.length === 0 && totalStrainCount === 0 ? (
              <p className="no-data">No strains available</p>
            ) : (
              <>
                <div className="genotype-list-container">
                    {isMobile ? (
                      <div className="mobile-list">
                        {data.strain.map((item) => (
                          <div key={item.id} className="list-item">
                            <div className="item-content">
                              <div className="item-title">{item.strain_name}</div>
                              <div className="item-subtitle">STRAIN</div>
                            </div>
                            <div className="item-actions">
                              <button
                                onClick={() => handleDeleteItem(item.id, item.strain_name, 'strain')}
                                className="action-btn"
                                style={{ backgroundColor: '#dc3545', color: 'white' }}
                                title={`Delete ${item.strain_name}`}
                                disabled={deletingId === item.id}
                              >
                                {deletingId === item.id ? 'Deleting...' : 'Delete'}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                    <table className="genotype-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort('strain', 'strain_name')}>
                                    Strain Name {sortColumnStrain === 'strain_name' ? (sortOrderStrain === 'asc' ? '▲' : '▼') : ''}
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.strain.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.strain_name}</td>
                                    <td>
                                        <button
                                            onClick={() => handleDeleteItem(item.id, item.strain_name, 'strain')}
                                            className="secondary-button"
                                            style={{
                                                backgroundColor: '#dc3545',
                                                padding: '5px 10px',
                                                fontSize: '14px',
                                                width: 'auto'
                                            }}
                                            title={`Delete ${item.strain_name}`}
                                            disabled={deletingId === item.id}
                                        >
                                            {deletingId === item.id ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    )}
                </div>

                {totalStrainCount > 0 && (
                  <div className="pagination-controls">
                    <div>
                      Items per page:
                      <select onChange={(e) => handleItemsPerPageChange('strain', e)} value={itemsPerPageStrain}>
                        {ITEMS_PER_PAGE_OPTIONS.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <button onClick={() => handlePageChange('strain', currentPageStrain - 1)} disabled={currentPageStrain === 1}>
                        Previous
                      </button>
                      <span>Page {currentPageStrain} of {totalPages}</span>
                      <button onClick={() => handlePageChange('strain', currentPageStrain + 1)} disabled={currentPageStrain === totalPages}>
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="genotype-tab-content">
        <h3>Manage {displayName} ({totalCount})</h3>
        
        <form onSubmit={handleAdd} className="add-item-form">
          <div className="form-group">
            <label>{displayName} Name(s):</label>
            <div className="input-group">
              <input
                type="text"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                placeholder={placeholderText}
                required
              />
              <button type="submit" disabled={isSubmitting}>{isSubmitting ? 'Adding...' : 'Add'}</button>
            </div>
          </div>
        </form>

                {error && <div className="error">{error}</div>}

        <div className="items-list">
          <h4>Current {displayName} Options</h4>
          {currentData.length === 0 && totalCount === 0 ? (
            <p className="no-data">No {displayName.toLowerCase()} options available</p>
          ) : (
            <>
              <div className="genotype-list-container">
                {isMobile ? renderMobileItemList() : (
                    <table className="genotype-table">
                        <thead>
                            <tr>
                                <th onClick={() => handleSort(activeTab, `${activeTab}_name`)}>
                                    {displayName} Name {sortColumn === `${activeTab}_name` ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentData.map((item) => (
                                <tr key={item.id}>
                                    <td>{item[`${activeTab}_name`] || 'Unknown'}</td>
                                    <td>
                                        <button
                                            onClick={() => handleDeleteItem(item.id, item[`${activeTab}_name`], activeTab)}
                                            className="secondary-button"
                                            style={{
                                                backgroundColor: '#dc3545',
                                                padding: '5px 10px',
                                                fontSize: '14px',
                                                width: 'auto'
                                            }}
                                            title={`Delete ${item[`${activeTab}_name`]}`}
                                            disabled={deletingId === item.id}
                                        >
                                            {deletingId === item.id ? 'Deleting...' : 'Delete'}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
              </div>

              {/* Pagination Controls */}
              {totalCount > 0 && (
                <div className="pagination-controls">
                  <div>
                    Items per page: 
                    <select onChange={(e) => handleItemsPerPageChange(activeTab, e)} value={itemsPerPage}>
                      {ITEMS_PER_PAGE_OPTIONS.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <button 
                      onClick={() => handlePageChange(activeTab, currentPage - 1)} 
                      disabled={currentPage === 1}
                    >
                      Previous
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button 
                      onClick={() => handlePageChange(activeTab, currentPage + 1)} 
                      disabled={currentPage === totalPages}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // Rendered outside the `loading` gate: a background sync completing while a
  // confirmation is open would otherwise unmount the dialog with its promise
  // still pending, silently swallowing the click.
  const confirmDialog = confirmDialogProps && <ConfirmDialog {...confirmDialogProps} />;

  if (loading) return <><div className="loading">Loading genotype data...</div>{confirmDialog}</>;

  return (
    <div className="genotype-management">
      {confirmDialog}
      <div className="tab-navigation">
        <button 
          className={activeTab === 'locus' ? 'active' : ''}
          onClick={() => setActiveTab('locus')}
        >
          Locus
        </button>
        <button 
          className={activeTab === 'allele' ? 'active' : ''}
          onClick={() => setActiveTab('allele')}
        >
          Allele
        </button>
        <button
          className={activeTab === 'pairs' ? 'active' : ''}
          onClick={() => setActiveTab('pairs')}
        >
          Colors
        </button>
        <button
          className={activeTab === 'strain' ? 'active' : ''}
          onClick={() => setActiveTab('strain')}
        >
          Strains
        </button>
      </div>

      {renderTabContent()}
    </div>
  );
};

export default GenotypeManagement;